import _ from 'lodash';

/* @ngInject */
export default function CalculateValuesService($log) {
  const vm = this;

  /**
   * Public functions
   */
  vm.calculateBarrels = calculateBarrels;
  vm.convertDecibels = convertDecibels;

  /**
   * A reading is either a plain number or, when the server downsampled the window, a
   * [min, avg, max] triple. Both of the calculations below are element-wise monotonic, so applying
   * them per component keeps a triple a valid triple.
   *
   * @param {number|Array<number>} reading a scalar reading or a [min, avg, max] triple
   * @returns {boolean} true when every component is a number
   */
  function isNumericReading(reading) {
    return _.isArray(reading) ? reading.length > 0 && _.every(reading, _.isNumber) : _.isNumber(reading);
  }

  /**
   * Apply `compute` to a scalar reading, or component-wise to a [min, avg, max] triple.
   *
   * @param {number|Array<number>} reading a scalar reading or a [min, avg, max] triple
   * @param {Function} compute maps one number to another
   * @returns {number|Array<number>} the mapped reading, matching the input shape
   */
  function mapReading(reading, compute) {
    return _.isArray(reading) ? _.map(reading, compute) : compute(reading);
  }

  /**
   * Pair up two readings component-wise (both scalars, or both triples).
   *
   * @param {number|Array<number>} left first reading
   * @param {number|Array<number>} right second reading
   * @param {Function} combine maps two numbers to one
   * @returns {number|Array<number>} the combined reading, matching the input shape
   */
  function zipReadings(left, right, combine) {
    return _.isArray(left) && _.isArray(right) ?
      _.map(left, (value, index) => combine(value, right[index])) :
      combine(left, right);
  }

  /**
   * Calculates the barrels data points using top level, water level, correction
   * factor and inches to barrels data points
   *
   * @param  {Array} events array of events containing data points from a tank equipment
   * @returns {Array} same as the parameter except each even contains an additional data points
   */
  function calculateBarrels(events) {
    _.forEach(events, ({ data }) => {
      const {
        topLevel,
        waterLevel,
        correctionFactor,
        inchesToBarrels
      } = data;
      // topLevel/waterLevel may be [min, avg, max] triples on a downsampled window; the correction
      // factor and inches-to-barrels are configuration and stay scalar.
      if (_.every([topLevel, waterLevel, correctionFactor, inchesToBarrels], isNumericReading)) {
        const toBarrels = level => (level + correctionFactor) * inchesToBarrels;
        data.topLevelInBbls = mapReading(topLevel, toBarrels);
        data.waterLevelInBbls = mapReading(waterLevel, toBarrels);
        data.oilLevelInBbls = zipReadings(
          data.topLevelInBbls, data.waterLevelInBbls, (top, water) => top - water
        );
      } else {
        $log.error('error with data points:', data);
      }
    });
    return events;
  }

  /**
   * Used to convert the real decibel readings from the Axis cameras in a negative
   * number to a positive representation. This 'add 90' was taken from the
   * Axis camera front-end GUI code, so it at least matches Axis' representation.
   *
   * @param  {Array} [events] containing data points from a camera equipment
   * @returns {Array} [events] with the data.decibel property converted
   */

  function convertDecibels(events) {
    const offset = 90;
    _.forEach(events, ({ data }) => {
      // On a downsampled window `decibel` is a [min, avg, max] triple; `+= 90` on an array would
      // string-concatenate, so shift each component instead.
      if (_.has(data, 'decibel') && isNumericReading(data.decibel)) {
        data.decibel = mapReading(data.decibel, value => value + offset);
      }
    });
    return events;
  }
}
